import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useSchemaOrg, getServiceSchema, getBreadcrumbSchema } from '../hooks/useSchemaOrg';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { includedFeatures } from '../data/mock';
import { getRouteBySlug, getVehicles, getRoutesFromLocation, transformVehicle, transformRoute } from '../services/api';
import { 
  MapPin, Clock, ChevronDown, ChevronUp, CheckCircle2, ArrowRight, Loader2, 
  Users, Briefcase, Shield, Phone,
  ChevronRight, HelpCircle, MessageSquare
} from 'lucide-react';

// Region colors
const REGION_COLORS = {
  'Messinia': 'bg-emerald-100 text-emerald-700',
  'Laconia': 'bg-purple-100 text-purple-700',
  'Arcadia': 'bg-blue-100 text-blue-700',
  'Argolida': 'bg-amber-100 text-amber-700',
  'Corinthia': 'bg-rose-100 text-rose-700',
  'Achaia': 'bg-cyan-100 text-cyan-700',
  'Ilia': 'bg-orange-100 text-orange-700',
};

// Destination images for route pages
const destinationImages = {
  'costa-navarino': 'https://res.cloudinary.com/dq8nrqgjn/image/upload/v1770053739/Costa_Navarino_x8zsq1.jpg',
  'stoupa': 'https://res.cloudinary.com/dq8nrqgjn/image/upload/v1770054216/Stoupa_sycdw9.jpg',
  'kardamyli': 'https://res.cloudinary.com/dq8nrqgjn/image/upload/v1770053745/Kardamyli_dmxome.jpg',
  'pilos': 'https://res.cloudinary.com/dq8nrqgjn/image/upload/v1770053741/Pylos_s4zowe.jpg',
  'methoni': 'https://res.cloudinary.com/dq8nrqgjn/image/upload/v1770053740/Methoni_x5uefq.jpg',
  'koroni': 'https://res.cloudinary.com/dq8nrqgjn/image/upload/v1770053739/Koroni_rwq6gw.jpg',
};

const RouteDetailPage = () => {
  const { fromSlug, toSlug } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [route, setRoute] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [otherRoutes, setOtherRoutes] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Change Route search form state
  const [showChangeRoute, setShowChangeRoute] = useState(false);
  const [searchFrom, setSearchFrom] = useState('');
  const [searchTo, setSearchTo] = useState('');
  const [searchFromQuery, setSearchFromQuery] = useState('');
  const [searchToQuery, setSearchToQuery] = useState('');
  const [showSearchFromDropdown, setShowSearchFromDropdown] = useState(false);
  const [showSearchToDropdown, setShowSearchToDropdown] = useState(false);
  
  // FAQ state
  const [openFaq, setOpenFaq] = useState(null);

  // Sort locations: Kalamata Airport first, then alphabetically
  const sortLocations = (locs) => {
    return [...locs].sort((a, b) => {
      if (a.slug === 'kalamata-airport') return -1;
      if (b.slug === 'kalamata-airport') return 1;
      return a.name.localeCompare(b.name);
    });
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);

        const [routeData, vehiclesData, otherRoutesData, locationsRes] = await Promise.all([
          getRouteBySlug(fromSlug, toSlug),
          getVehicles(),
          getRoutesFromLocation(fromSlug).catch(() => []),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/locations`).then(r => r.json()).catch(() => [])
        ]);

        setRoute(routeData);
        setLocations(locationsRes);
        
        const transformedVehicles = vehiclesData.map(v => {
          const tv = transformVehicle(v);
          const vehiclePrices = routeData.vehicle_prices || [];
          const vehiclePrice = vehiclePrices.find(vp => vp.vehicle_id === v.vehicle_id);
          tv.price = vehiclePrice ? vehiclePrice.price : (routeData.base_price || 0);
          tv.isCustomPrice = vehiclePrice ? vehiclePrice.is_custom === true : false;
          return tv;
        });
        setVehicles(transformedVehicles);

        const filteredRoutes = otherRoutesData
          .filter(r => r.to_slug !== toSlug)
          .slice(0, 4)
          .map(transformRoute);
        setOtherRoutes(filteredRoutes);

      } catch (err) {
        console.error('Error fetching route data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchData();

    // Reset search form state when route changes
    setShowChangeRoute(false);
    setSearchFrom('');
    setSearchTo('');
    setSearchFromQuery('');
    setSearchToQuery('');

    // Close dropdowns on outside click
    const handleClick = () => {
      setShowSearchFromDropdown(false);
      setShowSearchToDropdown(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [fromSlug, toSlug]);

  // Get region for route
  const getLocationRegion = (slug) => {
    const loc = locations.find(l => l.slug === slug);
    return loc?.region || 'Messinia';
  };

  const region = route ? getLocationRegion(route.to_slug) || getLocationRegion(route.from_slug) : 'Messinia';
  const regionColor = REGION_COLORS[region] || 'bg-gray-100 text-gray-700';

  // Handle vehicle selection - navigate to checkout
  const handleVehicleSelect = (vehicle) => {
    navigate('/checkout', {
      state: {
        route: {
          from_slug: route.from_slug,
          to_slug: route.to_slug,
          from_name: route.from_name,
          to_name: route.to_name,
          distance: route.distance,
          duration: route.duration
        },
        vehicle: vehicle,
        region: region
      }
    });
  };

  // Generate FAQs
  const generateFAQs = () => {
    if (!route) return [];
    
    const routeSpecificFAQs = [
      {
        question: `How long does the transfer from ${route.from_name} to ${route.to_name} take?`,
        answer: `The journey from ${route.from_name} to ${route.to_name} takes approximately ${route.duration}. The exact duration may vary slightly depending on traffic conditions.`
      },
      {
        question: `What is the distance from ${route.from_name} to ${route.to_name}?`,
        answer: `The distance from ${route.from_name} to ${route.to_name} is approximately ${route.distance}. Our drivers take the most efficient route to ensure a comfortable journey.`
      },
      {
        question: `How much does a transfer from ${route.from_name} to ${route.to_name} cost?`,
        answer: `Our transfer prices start from €${route.min_price || route.base_price} for a standard car. We offer different vehicle options including executive cars and vans for larger groups. All prices are fixed with no hidden fees.`
      }
    ];

    const generalFAQs = [
      {
        question: 'What happens if my flight is delayed?',
        answer: 'We monitor all flights in real-time. If your flight is delayed, your driver will automatically adjust their arrival time at no extra charge. You don\'t need to contact us - we\'ll be there when you land.'
      },
      {
        question: 'Can I cancel or modify my booking?',
        answer: 'Yes, you can cancel your booking free of charge up to 24 hours before your scheduled pickup time. For modifications, please contact us via WhatsApp or email and we\'ll do our best to accommodate your request.'
      },
      {
        question: 'What payment methods do you accept?',
        answer: 'We accept all major credit and debit cards through our secure online payment system. You can choose to pay the full amount upfront or just a 25% deposit to secure your booking.'
      },
      {
        question: 'Are child seats available?',
        answer: 'Yes, we provide child seats and baby seats free of charge. Simply select the number of seats you need when making your booking. We have seats suitable for babies (0-1 years) and children (1-7 years).'
      },
      {
        question: 'Where will my driver meet me?',
        answer: 'For airport pickups, your driver will wait for you in the arrivals hall holding a sign with your name. For hotel pickups, they will meet you at the hotel lobby or reception area at your scheduled time.'
      }
    ];

    return [...routeSpecificFAQs, ...generalFAQs];
  };

  // Compute SEO values (safe even when route is null)
  const routeData = route ? {
    from: route.from_name,
    to: route.to_name,
    fromSlug: route.from_slug,
    toSlug: route.to_slug,
    distance: route.distance,
    duration: route.duration,
    priceFrom: route.min_price || route.base_price,
  } : null;

  // SEO values with fallbacks
  const seoTitle = route?.seo_title || (route ? `${route.from_name} to ${route.to_name} Transfer | From €${routeData?.priceFrom} | Kalamata Airport Taxi` : 'Loading... | Kalamata Airport Taxi');
  const seoDescription = route?.seo_description || (route ? `Book your private transfer from ${route.from_name} to ${route.to_name}. ${route.distance}, ${route.duration} journey. Professional drivers, flight monitoring, fixed prices from €${routeData?.priceFrom}.` : 'Loading route details...');
  const canonicalUrl = route ? `https://kalamataairporttaxi.com/routes/${route.from_slug}/${route.to_slug}` : '';

  // Set document meta tags (must be called unconditionally - React hooks rule)
  useDocumentMeta({
    title: seoTitle,
    description: seoDescription,
    canonical: canonicalUrl,
    ogTitle: seoTitle,
    ogDescription: seoDescription,
    ogUrl: canonicalUrl,
    twitterTitle: seoTitle,
    twitterDescription: seoDescription
  });

  // Schema.org structured data for SEO (Service + Breadcrumb)
  useSchemaOrg(route ? [
    getServiceSchema(route, vehicles),
    getBreadcrumbSchema([
      { name: 'Home', url: 'https://kalamataairporttaxi.com' },
      { name: 'Routes', url: 'https://kalamataairporttaxi.com/routes' },
      { name: `${route.from_name} to ${route.to_name}`, url: canonicalUrl }
    ])
  ] : null);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="py-32 text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#36827f] mx-auto mb-4" />
          <p className="text-gray-600">Loading route details...</p>
        </main>
        <Footer />
      </div>
    );
  }

  // Error state
  if (error || !route) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        {/* Dark hero section for header background */}
        <div className="bg-[#2d232e] pt-40 pb-32">
          <div className="max-w-md mx-auto text-center px-4">
            <div className="w-20 h-20 mx-auto bg-[#36827f]/20 rounded-full flex items-center justify-center mb-6">
              <MapPin className="h-10 w-10 text-[#36827f]" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">Route Not Found</h1>
            <p className="text-white/70 mb-6">The requested route could not be found.</p>
            <Link to="/routes" className="inline-flex items-center gap-2 bg-[#36827f] hover:bg-[#2d6f6c] text-white px-6 py-3 rounded-xl font-semibold transition">
              View All Routes
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const faqs = generateFAQs();

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-[#2d232e] pt-28 pb-8">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#36827f]/10 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#36827f]/5 rounded-full blur-3xl pointer-events-none"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-white/60 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link to="/routes" className="hover:text-white transition-colors">Routes</Link>
              <ChevronRight className="h-4 w-4" />
              <span className="text-[#36827f]">{route.to_name}</span>
            </nav>

            {/* Region Badge */}
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-4 ${regionColor}`}>
              {region}
            </span>

            {/* Route Direction with Vertical Line */}
            <div className="flex gap-5 mb-6">
              <div className="flex flex-col items-center py-1">
                <div className="w-4 h-4 rounded-full bg-[#36827f]"></div>
                <div className="w-0.5 flex-1 bg-[#36827f]/40 my-2 min-h-[40px]"></div>
                <div className="w-4 h-4 rounded-full bg-[#36827f]/50"></div>
              </div>
              
              <div className="flex flex-col justify-between">
                <div className="pb-3">
                  <span className="text-white/50 text-sm">From</span>
                  <h1 className="text-2xl md:text-3xl font-bold text-white">{route.from_name}</h1>
                </div>
                <div className="pt-3">
                  <span className="text-white/50 text-sm">To</span>
                  <h2 className="text-2xl md:text-3xl font-bold text-[#36827f]">{route.to_name}</h2>
                </div>
              </div>
            </div>

            {/* Stats Row */}
            <div className="flex flex-wrap items-center gap-6 text-white/80 mb-6">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-[#36827f]" />
                <span>{routeData.distance}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-[#36827f]" />
                <span>{routeData.duration}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white/50">from</span>
                <span className="text-2xl font-bold text-[#36827f]">€{routeData.priceFrom}</span>
              </div>
            </div>

            {/* Change Route Link */}
            <button
              type="button"
              onClick={() => setShowChangeRoute(!showChangeRoute)}
              className="flex items-center gap-2 text-[#36827f] hover:text-white transition-colors mb-4"
            >
              <span className="text-sm font-medium">Change Route</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${showChangeRoute ? 'rotate-180' : ''}`} />
            </button>

            {/* Change Route Search Form - Expandable */}
            {showChangeRoute && (
              <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl p-4 max-w-3xl relative z-[200]">
                <div className="flex flex-col lg:flex-row items-stretch">
                  {/* From */}
                  <div className="relative flex-[1.5] min-w-0" onClick={(e) => e.stopPropagation()}>
                    <div 
                      className="flex items-center gap-3 px-5 py-5 hover:bg-gray-50 transition rounded-xl h-full cursor-pointer"
                      onClick={() => { setShowSearchFromDropdown(true); setShowSearchToDropdown(false); }}
                    >
                      <MapPin className="w-6 h-6 text-[#36827f] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">From</p>
                        <input
                          type="text"
                          placeholder="Select pickup location"
                          value={showSearchFromDropdown ? searchFromQuery : searchFrom}
                          onChange={(e) => { setSearchFromQuery(e.target.value); setShowSearchFromDropdown(true); }}
                          onFocus={() => { setShowSearchFromDropdown(true); setSearchFromQuery(''); }}
                          className={`bg-transparent outline-none w-full truncate placeholder-gray-400 ${searchFrom && !showSearchFromDropdown ? 'font-medium text-gray-900' : 'text-gray-900'}`}
                        />
                      </div>
                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${showSearchFromDropdown ? 'rotate-180' : ''}`} />
                    </div>
                    {showSearchFromDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-[300] max-h-[280px] overflow-y-auto">
                        {sortLocations(locations
                          .filter(loc => !searchFromQuery || loc.name.toLowerCase().includes(searchFromQuery.toLowerCase()) || loc.region?.toLowerCase().includes(searchFromQuery.toLowerCase()))
                        ).map(loc => (
                            <div 
                              key={loc.slug}
                              className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition"
                              onClick={() => { setSearchFrom(loc.name); setSearchFromQuery(''); setShowSearchFromDropdown(false); }}
                            >
                              <p className="font-medium text-gray-900">{loc.name}</p>
                              <p className="text-sm text-gray-500">{loc.region}</p>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>

                  <div className="hidden lg:block w-px bg-gray-200 my-3" />

                  {/* To */}
                  <div className="relative flex-[1.5] min-w-0" onClick={(e) => e.stopPropagation()}>
                    <div 
                      className="flex items-center gap-3 px-5 py-5 hover:bg-gray-50 transition rounded-xl h-full cursor-pointer"
                      onClick={() => { setShowSearchToDropdown(true); setShowSearchFromDropdown(false); }}
                    >
                      <MapPin className="w-6 h-6 text-[#36827f] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">To</p>
                        <input
                          type="text"
                          placeholder="Select destination"
                          value={showSearchToDropdown ? searchToQuery : searchTo}
                          onChange={(e) => { setSearchToQuery(e.target.value); setShowSearchToDropdown(true); }}
                          onFocus={() => { setShowSearchToDropdown(true); setSearchToQuery(''); }}
                          className={`bg-transparent outline-none w-full truncate placeholder-gray-400 ${searchTo && !showSearchToDropdown ? 'font-medium text-gray-900' : 'text-gray-900'}`}
                        />
                      </div>
                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${showSearchToDropdown ? 'rotate-180' : ''}`} />
                    </div>
                    {showSearchToDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-[300] max-h-[280px] overflow-y-auto">
                        {sortLocations(locations
                          .filter(loc => !searchToQuery || loc.name.toLowerCase().includes(searchToQuery.toLowerCase()) || loc.region?.toLowerCase().includes(searchToQuery.toLowerCase()))
                        ).map(loc => (
                            <div 
                              key={loc.slug}
                              className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition"
                              onClick={() => { setSearchTo(loc.name); setSearchToQuery(''); setShowSearchToDropdown(false); }}
                            >
                              <p className="font-medium text-gray-900">{loc.name}</p>
                              <p className="text-sm text-gray-500">{loc.region}</p>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>

                  {/* Search Button */}
                  <button 
                    type="button"
                    onClick={() => {
                      if (searchFrom && searchTo) {
                        const fromSlugNew = locations.find(l => l.name === searchFrom)?.slug;
                        const toSlugNew = locations.find(l => l.name === searchTo)?.slug;
                        if (fromSlugNew && toSlugNew) {
                          navigate(`/routes/${fromSlugNew}/${toSlugNew}`);
                        }
                      }
                    }}
                    disabled={!searchFrom || !searchTo}
                    className="m-3 px-10 py-5 rounded-xl font-bold text-white text-lg transition-all bg-[#36827f] hover:bg-[#2d6f6c] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#36827f]/20 hover:shadow-xl"
                  >
                    Search
                  </button>
                </div>
              </div>
            )}

            {/* Progress Indicator */}
            <div className="flex items-center gap-3 mt-6 pt-6 border-t border-white/10">
              {/* Step 1 - Current */}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#36827f] flex items-center justify-center ring-4 ring-[#36827f]/30">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <span className="text-white font-medium hidden sm:inline">Select Vehicle</span>
              </div>
              
              <div className="flex-1 h-0.5 bg-white/20 max-w-[60px]"></div>
              
              {/* Step 2 - Pending */}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <span className="text-white/60 font-bold text-sm">2</span>
                </div>
                <span className="text-white/60 font-medium hidden sm:inline">Booking Details</span>
              </div>
              
              <div className="flex-1 h-0.5 bg-white/20 max-w-[60px]"></div>
              
              {/* Step 3 - Pending */}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <span className="text-white/60 font-bold text-sm">3</span>
                </div>
                <span className="text-white/60 font-medium hidden sm:inline">Payment</span>
              </div>
            </div>
          </div>
        </section>

        {/* Select Vehicle Section - Full Width */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-xl bg-[#36827f]/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-[#36827f]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Select Vehicle</h2>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {vehicles.map((vehicle) => (
                <button
                  key={vehicle.id}
                  type="button"
                  onClick={() => handleVehicleSelect(vehicle)}
                  data-testid={`vehicle-${vehicle.id}`}
                  className="group bg-white rounded-2xl border-2 border-gray-200 hover:border-[#36827f] hover:shadow-xl transition-all duration-300 overflow-hidden text-left flex flex-col"
                >
                  {/* Vehicle Image */}
                  <div className="w-full h-48 sm:h-52 bg-gray-50 overflow-hidden flex items-center justify-center p-4">
                    <img 
                      src={vehicle.image} 
                      alt={vehicle.type}
                      className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  
                  {/* Vehicle Info */}
                  <div className="p-5 sm:p-6 flex flex-col flex-1 min-h-[200px]">
                    <h3 className="font-bold text-gray-900 text-lg mb-2">{vehicle.type}</h3>
                    <p className="text-sm text-gray-500 mb-5">{vehicle.subtitle}</p>
                    
                    {/* Capacity */}
                    <div className="flex items-center gap-4 text-gray-600 text-sm mb-5">
                      <div className="flex items-center gap-1.5">
                        <Users className="h-4 w-4 text-[#36827f]" />
                        <span>{vehicle.passengers} passengers</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Briefcase className="h-4 w-4 text-[#36827f]" />
                        <span>{vehicle.luggage || vehicle.passengers} bags</span>
                      </div>
                    </div>
                    
                    {/* Price Button */}
                    <div className="mt-auto">
                      <div className="w-full text-white text-center py-3.5 rounded-xl font-bold text-lg transition-colors bg-[#36827f] group-hover:bg-[#2d6f6c]">
                        €{vehicle.price}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* About This Route + What's Included */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Mobile Route Map - Shows only on mobile, above About section */}
            <div className="lg:hidden mb-6">
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <img 
                  src={`/route-maps/${fromSlug === 'kalamata-airport' ? toSlug : `${fromSlug}-return`}.png`}
                  alt={`Route from ${route.from_name} to ${route.to_name}`}
                  className="w-full h-auto"
                  onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* About This Route */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl border border-gray-200 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-[#36827f]/10 flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-[#36827f]" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">About This Route</h2>
                  </div>

                  {/* Destination Image */}
                  {(() => {
                    const destinationSlug = fromSlug === 'kalamata-airport' ? toSlug : fromSlug;
                    const imageUrl = destinationImages[destinationSlug];
                    return null; // Image moved below
                  })()}
                  
                  <div className="text-gray-600 leading-relaxed space-y-4">
                    {/* Different content for outbound vs return trips */}
                    {toSlug === 'kalamata-airport' ? (
                      <>
                        {/* RETURN TRIP: Destination → Airport */}
                        <p>
                          Heading back to <strong>Kalamata Airport</strong> from <strong>{route.from_name}</strong>? Our return transfer service ensures you reach the airport relaxed and with plenty of time for check-in. End your Messenia trip as smoothly as it started.
                        </p>
                        
                        <p>
                          Your driver will collect you from your accommodation at the agreed time, help with all luggage, and take the most efficient route to the airport. We monitor your flight schedule and recommend pickup times with a comfortable buffer, so you never have to worry about missing your departure.
                        </p>

                        <div className="pt-4">
                          <h3 className="font-bold text-gray-900 text-lg mb-3">Why Book Your Return Transfer</h3>
                          <ul className="space-y-3">
                            <li className="flex items-start gap-3">
                              <CheckCircle2 className="h-5 w-5 text-[#36827f] flex-shrink-0 mt-0.5" />
                              <span><strong className="text-gray-900">Punctual Pickup:</strong> We arrive at your accommodation on time, every time. No stress about finding a taxi on departure day.</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <CheckCircle2 className="h-5 w-5 text-[#36827f] flex-shrink-0 mt-0.5" />
                              <span><strong className="text-gray-900">Flight Monitoring:</strong> We track your departure flight. If it's delayed, we adjust your pickup time accordingly.</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <CheckCircle2 className="h-5 w-5 text-[#36827f] flex-shrink-0 mt-0.5" />
                              <span><strong className="text-gray-900">Door-to-Door Service:</strong> From your hotel, villa, or accommodation directly to the airport terminal.</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <CheckCircle2 className="h-5 w-5 text-[#36827f] flex-shrink-0 mt-0.5" />
                              <span><strong className="text-gray-900">Fixed Pricing:</strong> Same great price as your outbound journey. No surge pricing, no surprises.</span>
                            </li>
                          </ul>
                        </div>

                        <div className="pt-4">
                          <h3 className="font-bold text-gray-900 text-lg mb-3">The Journey</h3>
                          <p>
                            The {route.distance} journey from {route.from_name} to Kalamata Airport takes approximately {route.duration}. We recommend being picked up at least 2 hours before domestic flights and 3 hours before international departures to allow comfortable time for check-in and security.
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* OUTBOUND TRIP: Airport → Destination */}
                        <p>
                          Experience a seamless and comfortable private transfer from <strong>{route.from_name}</strong> to <strong>{route.to_name}</strong> with our premium transfer service. Our professional chauffeur service ensures you arrive relaxed and ready to enjoy your Greek holiday.
                        </p>
                        
                        <p>
                          Upon arrival, your English-speaking driver will be waiting at the arrivals hall with a personalized name board. After a warm welcome, they will assist with your luggage and escort you to your private, air-conditioned vehicle. The scenic {route.duration} journey takes you through the beautiful Messenian countryside.
                        </p>

                        <div className="pt-4">
                          <h3 className="font-bold text-gray-900 text-lg mb-3">Why Choose Our Transfer Service</h3>
                          <ul className="space-y-3">
                            <li className="flex items-start gap-3">
                              <CheckCircle2 className="h-5 w-5 text-[#36827f] flex-shrink-0 mt-0.5" />
                              <span><strong className="text-gray-900">Flight Monitoring:</strong> We track your flight in real-time. If delayed, your driver adjusts accordingly at no extra charge.</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <CheckCircle2 className="h-5 w-5 text-[#36827f] flex-shrink-0 mt-0.5" />
                              <span><strong className="text-gray-900">No Waiting:</strong> Skip the taxi queues. Your vehicle is reserved exclusively for you.</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <CheckCircle2 className="h-5 w-5 text-[#36827f] flex-shrink-0 mt-0.5" />
                              <span><strong className="text-gray-900">Local Expertise:</strong> Our drivers know the region and can recommend restaurants, beaches, and hidden gems.</span>
                            </li>
                            <li className="flex items-start gap-3">
                              <CheckCircle2 className="h-5 w-5 text-[#36827f] flex-shrink-0 mt-0.5" />
                              <span><strong className="text-gray-900">Fixed Pricing:</strong> The price you see is the price you pay. No surge pricing, no surprises.</span>
                            </li>
                          </ul>
                        </div>

                        <div className="pt-4">
                          <h3 className="font-bold text-gray-900 text-lg mb-3">The Route</h3>
                          <p>
                            The {route.distance} journey from {route.from_name} to {route.to_name} follows scenic roads through Messenia, one of Greece's most unspoiled regions. You'll pass through charming villages and alongside pristine beaches before arriving at your destination.
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Destination Image - Below content */}
                  {(() => {
                    const destinationSlug = fromSlug === 'kalamata-airport' ? toSlug : fromSlug;
                    const imageUrl = destinationImages[destinationSlug];
                    return imageUrl ? (
                      <div className="mt-6 rounded-xl overflow-hidden">
                        <img 
                          src={imageUrl}
                          alt={fromSlug === 'kalamata-airport' ? route.to_name : route.from_name}
                          className="w-full h-72 object-cover"
                        />
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>

              {/* What's Included - Sticky Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  {/* Route Map Image - Hidden on mobile (shown above on mobile) */}
                  <div className="hidden lg:block bg-white rounded-2xl border border-gray-200 overflow-hidden mb-6">
                    <img 
                      src={`/route-maps/${fromSlug === 'kalamata-airport' ? toSlug : `${fromSlug}-return`}.png`}
                      alt={`Route from ${route.from_name} to ${route.to_name}`}
                      className="w-full h-auto"
                      onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                    />
                  </div>

                  <div className="bg-white rounded-2xl border border-gray-200 p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">What's Included</h3>
                    </div>
                    
                    <div className="space-y-3">
                      {includedFeatures.map((feature, index) => (
                        <div key={index} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                          <span className="text-gray-700 font-medium text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <div className="flex items-center gap-3 text-gray-600 mb-3">
                        <Shield className="h-5 w-5 text-[#36827f]" />
                        <span className="font-medium">Book with Confidence</span>
                      </div>
                      <p className="text-sm text-gray-500">
                        Free cancellation up to 24 hours before your scheduled pickup. Our team is available 24/7 to assist you.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Other Routes */}
        {otherRoutes.length > 0 && (
          <section className="py-12 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 bg-[#36827f]/10 rounded-full px-4 py-2 mb-4">
                  <MapPin className="h-4 w-4 text-[#36827f]" />
                  <span className="text-sm font-semibold text-[#36827f]">More Options</span>
                </div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Other Routes from <span className="text-[#36827f]">{route.from_name}</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {otherRoutes.map((r) => (
                  <Link
                    key={r.id}
                    to={`/routes/${fromSlug}/${r.toSlug}`}
                    className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#36827f]/30 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <p className="font-bold text-gray-900 group-hover:text-[#36827f] transition-colors">
                        {r.to}
                      </p>
                      <p className="text-xl font-bold text-[#36827f]">€{r.price}</p>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {r.distance}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {r.duration}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="text-center mt-10">
                <Link 
                  to="/routes"
                  className="inline-flex items-center gap-2 border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-8 py-4 font-bold rounded-xl transition-all"
                >
                  View All Routes
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* FAQs Section */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-[#36827f]/10 rounded-full px-4 py-2 mb-4">
                <HelpCircle className="h-4 w-4 text-[#36827f]" />
                <span className="text-sm font-semibold text-[#36827f]">FAQs</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                Frequently Asked <span className="text-[#36827f]">Questions</span>
              </h2>
              <p className="text-gray-600 mt-2">Everything you need to know about this transfer</p>
            </div>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl shadow-sm overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition"
                  >
                    <span className="font-semibold text-[#2d232e] pr-4">{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-[#36827f] flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Contact CTA */}
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-4">Still have questions?</p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <a 
                  href="tel:+306932421170"
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#36827f] text-white font-semibold hover:bg-[#2d6f6c] transition"
                >
                  <Phone className="w-5 h-5" />
                  Call Us
                </a>
                <a 
                  href="https://wa.me/306932421170"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#25D366] text-white font-semibold hover:bg-[#20bd5a] transition"
                >
                  <MessageSquare className="w-5 h-5" />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Return/Outbound Trip Link Banner */}
        <section className="bg-gradient-to-r from-[#36827f]/5 to-[#36827f]/10 border-y border-[#36827f]/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <Link 
              to={`/routes/${toSlug}/${fromSlug}`}
              className="flex items-center justify-between group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#36827f]/10 flex items-center justify-center group-hover:bg-[#36827f]/20 transition-colors">
                  <ArrowRight className="h-5 w-5 text-[#36827f] rotate-180 group-hover:rotate-0 transition-transform duration-300" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    {fromSlug === 'kalamata-airport' ? 'Need the return trip?' : 'Looking for the outbound trip?'}
                  </p>
                  <p className="font-semibold text-gray-900 group-hover:text-[#36827f] transition-colors">
                    {fromSlug === 'kalamata-airport' 
                      ? `Book ${route.to_name} → Kalamata Airport`
                      : `Book Kalamata Airport → ${route.from_name}`
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-[#36827f] font-medium">
                <span className="hidden sm:inline">View Route</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default RouteDetailPage;
